import type { LinkSnapshot } from '../stores/types';

export type IndexedGanttLink = {
  id: number;
  source: string;
  target: string;
  type: string;
  lag: number | null;
};

export type GanttLinkEndpointIndex = {
  /** target task id → predecessor links */
  byTarget: Map<string, IndexedGanttLink[]>;
  /** source task id → successor links */
  bySource: Map<string, IndexedGanttLink[]>;
};

function toIndexedLink(link: {
  id?: number | string;
  source: string | number;
  target: string | number;
  type?: string | number;
  lag?: number | null;
}): IndexedGanttLink {
  return {
    id: Number(link.id) || 0,
    source: String(link.source),
    target: String(link.target),
    type: String(link.type ?? '0'),
    lag: link.lag ?? null,
  };
}

/** Build O(1) predecessor/successor lookups from a link list (keeps type + lag). */
export function buildGanttLinkEndpointIndex(
  links: Array<{
    id?: number | string;
    source: string | number;
    target: string | number;
    type?: string | number;
    lag?: number | null;
  }>,
): GanttLinkEndpointIndex {
  const byTarget = new Map<string, IndexedGanttLink[]>();
  const bySource = new Map<string, IndexedGanttLink[]>();
  for (const raw of links) {
    const link = toIndexedLink(raw);
    let preds = byTarget.get(link.target);
    if (!preds) {
      preds = [];
      byTarget.set(link.target, preds);
    }
    preds.push(link);
    let succs = bySource.get(link.source);
    if (!succs) {
      succs = [];
      bySource.set(link.source, succs);
    }
    succs.push(link);
  }
  return { byTarget, bySource };
}

/** True when gantt and store describe the same dependency graph (endpoints + type + lag). */
export function linkGraphsEquivalent(
  storeLinks: LinkSnapshot[],
  ganttLinks: Array<{
    source: string | number;
    target: string | number;
    type?: string | number;
    lag?: number | null;
  }>,
): boolean {
  if (storeLinks.length !== ganttLinks.length) return false;
  const keys = new Set(
    storeLinks.map(
      (l) => `${String(l.source)}|${String(l.target)}|${String(l.type ?? '0')}|${l.lag ?? ''}`,
    ),
  );
  for (const l of ganttLinks) {
    const key = `${String(l.source)}|${String(l.target)}|${String(l.type ?? '0')}|${l.lag ?? ''}`;
    if (!keys.has(key)) return false;
  }
  return true;
}
